import { ApiClientError, apiClient } from '../../core/api';

export const PROFILE_IDENTITY_TIMEOUT_MS = 3000;

/** Bound profile lookup latency without changing the shared API client. */
async function currentProfileUser() {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      apiClient.currentUser(),
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(
          () => reject(new Error('确认当前账户超时，请稍后重试')),
          PROFILE_IDENTITY_TIMEOUT_MS,
        );
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

/** Local to this browser and keyed exclusively by the authenticated user's ID. */
export interface PersonalProfile {
  userId: string;
  nickname: string;
  birthday: string;
  interests: string;
  bio: string;
  allowAgentRead: boolean;
  /** Cropped local image; never included in the model's personal-profile text. */
  avatar?: string;
}

export type ProfileDraft = Omit<PersonalProfile, 'userId'>;

export function emptyProfile(userId: string): PersonalProfile {
  return { userId, nickname: '', birthday: '', interests: '', bio: '', allowAgentRead: false };
}

export function profileStorageKey(userId: string): string {
  if (userId.trim() === '') throw new Error('无法识别当前账户');
  return `zhixin.personal-profile.v1:${encodeURIComponent(userId)}`;
}

function normalizeDraft(draft: ProfileDraft): ProfileDraft {
  const limits = { nickname: 80, birthday: 10, interests: 500, bio: 2000 } as const;
  const fields = {} as Pick<ProfileDraft, keyof typeof limits>;
  for (const field of Object.keys(limits) as (keyof typeof limits)[]) {
    if (typeof draft[field] !== 'string') throw new Error('个人资料格式不合法');
    fields[field] = draft[field].trim();
    if (fields[field].length > limits[field]) throw new Error('个人资料超出允许长度');
  }
  if (fields.birthday !== '') {
    const date = new Date(`${fields.birthday}T00:00:00Z`);
    if (
      !/^\d{4}-\d{2}-\d{2}$/.test(fields.birthday) ||
      !Number.isFinite(date.getTime()) ||
      date.toISOString().slice(0, 10) !== fields.birthday
    ) {
      throw new Error('请输入有效生日');
    }
  }
  // A missing flag is never consent. Reject truthy non-booleans, including "true".
  if (draft.allowAgentRead !== undefined && typeof draft.allowAgentRead !== 'boolean') {
    throw new Error('个人资料读取授权格式不合法');
  }
  const avatar = draft.avatar?.trim();
  if (avatar && (!/^data:image\/(png|jpeg|webp);base64,[a-z0-9+/=]+$/i.test(avatar) || avatar.length > 2 * 1024 * 1024)) {
    throw new Error('头像格式不合法或图片过大，请重新裁剪');
  }
  return { ...fields, allowAgentRead: draft.allowAgentRead === true, ...(avatar ? { avatar } : {}) };
}

export function loadProfile(userId: string): PersonalProfile | null {
  const text = localStorage.getItem(profileStorageKey(userId));
  if (text === null) return null;
  const value: unknown = JSON.parse(text);
  if (
    typeof value !== 'object' ||
    value === null ||
    Array.isArray(value) ||
    !('userId' in value) ||
    value.userId !== userId
  ) {
    throw new Error('个人资料账户不匹配，请重新保存');
  }
  return { userId, ...normalizeDraft(value as PersonalProfile) };
}

/** Resolve the current session for every send; never cache a previous account's profile. */
export async function loadActiveProfile(): Promise<PersonalProfile | null> {
  try {
    const user = await currentProfileUser();
    if (user.status === 'disabled') return null;
    return loadProfile(user.id);
  } catch (error) {
    if (error instanceof ApiClientError && error.status === 401) return null;
    throw error;
  }
}

/** Recheck identity after editing, before any synchronous local write. */
export async function saveProfile(userId: string, draft: ProfileDraft): Promise<PersonalProfile> {
  const user = await currentProfileUser();
  if (user.id !== userId || user.status === 'disabled') {
    throw new Error('登录账户已变化，请重新打开个人资料');
  }
  const profile: PersonalProfile = { userId, ...normalizeDraft(draft) };
  localStorage.setItem(profileStorageKey(userId), JSON.stringify(profile));
  window.dispatchEvent(new Event('zhixin:profile-saved'));
  return profile;
}
